import { HttpErrorResponse } from '@angular/common/http';

interface ValidationIssue {
  loc?: unknown[];
  msg?: unknown;
  message?: unknown;
}

const TECHNICAL_MESSAGE = /(?:traceback|sqlalchemy|integrityerror|exception|stack trace|internal server error|http failure response|database operation)/i;
const GENERIC_BY_STATUS: Record<number, string> = {
  400: 'We could not process this request. Please check the entered details and try again.',
  401: 'Your session has expired. Please sign in again to continue.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested record could not be found. It may have been moved or removed.',
  405: 'This action is not currently supported.',
  408: 'The request took too long. Please try again.',
  409: 'This change conflicts with existing information. Please review the details and try again.',
  413: 'The selected file is too large. Please choose a smaller file and try again.',
  415: 'This file type is not supported. Please choose an allowed file format.',
  422: 'Some information is missing or invalid. Please review the highlighted fields.',
  429: 'Too many requests were made. Please wait a moment and try again.',
  500: 'We could not complete your request right now. Please try again later.',
  502: 'The service is temporarily unavailable. Please try again shortly.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: 'The service took too long to respond. Please try again.',
};

function readableField(location: unknown[] | undefined): string {
  const raw = location?.filter((item) => item !== 'body' && item !== 'query' && item !== 'path').at(-1);
  if (typeof raw !== 'string') return '';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function validationMessage(detail: unknown): string | null {
  if (!Array.isArray(detail)) return null;
  const messages = detail
    .map((item: ValidationIssue) => {
      const message = typeof item?.msg === 'string'
        ? item.msg.replace(/^Value error,\s*/i, '')
        : typeof item?.message === 'string' ? item.message : '';
      if (!message) return '';
      const field = readableField(item.loc);
      if (/^field required$/i.test(message)) return field ? `${field} is required.` : 'A required field is missing.';
      return field && !message.toLowerCase().includes(field.toLowerCase()) ? `${field}: ${message}` : message;
    })
    .filter(Boolean);
  return messages.length ? [...new Set(messages)].slice(0, 3).join(' ') : null;
}

function safeServerMessage(error: HttpErrorResponse): string | null {
  const body = error.error;
  const candidates = [body?.message, typeof body?.detail === 'string' ? body.detail : null];
  const message = candidates.find((value): value is string => typeof value === 'string' && Boolean(value.trim()))?.trim();
  if (!message || TECHNICAL_MESSAGE.test(message) || /^\d{3}\b/.test(message)) return null;
  return message;
}

export function userFriendlyHttpError(error: HttpErrorResponse): string {
  if (error.status === 0) {
    return typeof navigator === 'undefined' || navigator.onLine
      ? 'We could not connect to the service. Please try again in a moment.'
      : 'You appear to be offline. Check your internet connection and try again.';
  }

  const validation = validationMessage(error.error?.detail ?? error.error?.errors);
  if (validation) return validation;

  const serverMessage = safeServerMessage(error);
  if (serverMessage) return serverMessage;

  return GENERIC_BY_STATUS[error.status]
    ?? (error.status >= 500
      ? 'We could not complete your request right now. Please try again later.'
      : 'Something went wrong. Please check your details and try again.');
}
